import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  CircularProgress,
  Typography,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../commons/AppLayout/AppLayout';
import RTVDialog from '../../commons/dialogs/RTVDialog';
import {
  subscribeToRtvPlus,
  resetRtvPlusSubscriptionState
} from '../../redux/actions/user';
import {
  SUBSCRIBE_RTV_PLUS_REQUESTED,
  SUBSCRIBE_RTV_PLUS_SUCCESSFUL,
  SUBSCRIBE_RTV_PLUS_ERRORED
} from '../../redux/actions/types';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 500,
    textAlign: 'center',
    '& > *': {
      margin: theme.spacing(2)
    }
  },
  title: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  message: {
    color: '#707070'
  },
  errorMessage: {
    color: theme.palette.error.main
  },
  dialogContent: {
    width: '100%'
  },
  dialogTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    textAlign: 'center',
    color: theme.palette.primary.main
  },
  dialogText: {
    fontSize: '0.9rem',
    color: '#707070',
    textAlign: 'center'
  },
  input: {
    marginTop: theme.spacing(2)
  },
  checkbox: {
    marginTop: theme.spacing(1)
  }
}));

const RTVPlusSubscribe = ({
  subscribeToRtvPlus,
  resetRtvPlusSubscriptionState,
  subscriptionStatus,
  subscriptionData,
  subscriptionError,
  isLoggedIn,
  userEmail,
  location,
  history
}) => {
  const classes = useStyles();
  const { t } = useTranslation('rtvPlus');
  const [hasInitiated, setHasInitiated] = useState(false);
  const [hasSeenRequested, setHasSeenRequested] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [mpEmail, setMpEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [noMpAccount, setNoMpAccount] = useState(false);

  useEffect(() => {
    if (isLoggedIn === false) {
      resetRtvPlusSubscriptionState();
      const from = encodeURIComponent(location.pathname + location.search);
      history.replace(`/login?from=${from}`);
    }
  }, [
    isLoggedIn,
    location.pathname,
    location.search,
    history,
    resetRtvPlusSubscriptionState
  ]);

  useEffect(() => {
    // Only initiate once when component mounts (and user is logged in)
    if (!isLoggedIn || !hasInitiated) {
      if (!isLoggedIn) return;
      setHasInitiated(true);
      setHasSeenRequested(false);

      const params = new URLSearchParams(location.search);

      // Check if returning from MercadoPago (these params are added by MP)
      const preapprovalId = params.get('preapproval_id');
      const externalReference = params.get('external_reference');
      const isReturningFromMP =
        preapprovalId || externalReference || params.get('collection_id');

      if (isReturningFromMP) {
        // User is returning from MercadoPago payment
        // The backend webhook should have already updated the subscription status
        // Just call the API which will sync the status and return current state
        subscribeToRtvPlus();
      } else {
        // Fresh subscription creation - send back_url
        setShowEmailDialog(true);
        setMpEmail(userEmail || '');
      }
    }
  }, [
    hasInitiated,
    isLoggedIn,
    subscribeToRtvPlus,
    location.search,
    userEmail
  ]);

  useEffect(() => {
    if (showEmailDialog && userEmail) {
      setMpEmail(current => current || userEmail);
    }
  }, [showEmailDialog, userEmail]);

  useEffect(() => {
    if (noMpAccount && userEmail) {
      setMpEmail(userEmail);
    }
  }, [noMpAccount, userEmail]);

  useEffect(() => {
    if (subscriptionStatus === SUBSCRIBE_RTV_PLUS_REQUESTED) {
      setHasSeenRequested(true);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (!hasSeenRequested) return;
    if (
      subscriptionStatus !== SUBSCRIBE_RTV_PLUS_SUCCESSFUL ||
      !subscriptionData
    )
      return;

    // Only redirect to MercadoPago if status is pending and we have init_point
    if (subscriptionData.status === 'pending' && subscriptionData.init_point) {
      setTimeout(() => {
        window.location.href = subscriptionData.init_point;
      }, 1000);
    }
  }, [hasSeenRequested, subscriptionStatus, subscriptionData]);

  useEffect(() => {
    if (
      subscriptionStatus === SUBSCRIBE_RTV_PLUS_SUCCESSFUL &&
      subscriptionData?.status === 'authorized'
    ) {
      history.replace('/home', { showRtvPlusWelcomeModal: true });
    }
  }, [subscriptionStatus, subscriptionData, history]);

  // 409 = already subscribed: treat as success, redirect to home with welcome modal
  useEffect(() => {
    if (
      subscriptionStatus === SUBSCRIBE_RTV_PLUS_ERRORED &&
      subscriptionError?.status === 409
    ) {
      history.replace('/home', { showRtvPlusWelcomeModal: true });
    }
  }, [subscriptionStatus, subscriptionError, history]);

  const processingModal = useMemo(() => {
    return (
      <>
        <CircularProgress size={60} />
        <Typography variant="h5" className={classes.title}>
          {t('subscribe.processingTitle')}
        </Typography>
        <Typography className={classes.message}>
          {t('subscribe.processingMessage')}
        </Typography>
      </>
    );
  }, [classes, t]);

  const renderContent = () => {
    if (subscriptionStatus === SUBSCRIBE_RTV_PLUS_REQUESTED) {
      return processingModal;
    }

    if (subscriptionStatus === SUBSCRIBE_RTV_PLUS_SUCCESSFUL) {
      if (subscriptionData?.status === 'authorized') {
        return null;
      }

      return processingModal;
    }

    if (subscriptionStatus === SUBSCRIBE_RTV_PLUS_ERRORED) {
      const statusCode = subscriptionError?.status;
      // 409 = already subscribed: we redirect in useEffect; show brief loading instead of "Ups"
      if (statusCode === 409) {
        return (
          <>
            <CircularProgress size={60} />
            <Typography variant="h5" className={classes.title}>
              {t('subscribe.redirecting')}
            </Typography>
          </>
        );
      }

      return (
        <>
          <Typography variant="h5" className={classes.title}>
            {t('subscribe.errorTitle')}
          </Typography>
          <Typography className={classes.errorMessage}>
            {t('subscribe.errorMessage')}
          </Typography>
        </>
      );
    }

    // Initial loading state
    return (
      <>
        <CircularProgress size={60} />
        <Typography variant="h5" className={classes.title}>
          {t('subscribe.initiating')}
        </Typography>
      </>
    );
  };

  return (
    <AppLayout title={t('subscribePageTitle')}>
      <div className={classes.root}>
        <RTVDialog
          open={showEmailDialog}
          onClose={() => {
            setShowEmailDialog(false);
            history.replace('/home');
          }}
          title={t('subscribe.dialogTitle')}
          titleStyles={classes.dialogTitle}
          content={
            <div className={classes.dialogContent}>
              <Typography className={classes.dialogText}>
                {t('subscribe.dialogText')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                label={t('subscribe.emailLabel')}
                value={mpEmail}
                onChange={e => {
                  setMpEmail(e.target.value);
                  if (emailError) {
                    setEmailError('');
                  }
                }}
                disabled={noMpAccount}
                className={classes.input}
                error={Boolean(emailError)}
                helperText={emailError || ''}
              />
              <FormControlLabel
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={noMpAccount}
                    onChange={e => setNoMpAccount(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('subscribe.noAccountLabel')}
              />
            </div>
          }
          actionSection={
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mpEmail);
                if (!isValidEmail) {
                  setEmailError(t('subscribe.invalidEmail'));
                  return;
                }

                setEmailError('');
                setShowEmailDialog(false);
                const params = new URLSearchParams(location.search);
                const defaultBackUrl = `${window.location.origin}/home`;
                const backUrl = params.get('back_url') || defaultBackUrl;
                subscribeToRtvPlus(backUrl, mpEmail);
              }}
            >
              {t('subscribe.continue')}
            </Button>
          }
        />
        <Paper className={classes.paper}>{renderContent()}</Paper>
      </div>
    </AppLayout>
  );
};

const mapStateToProps = state => ({
  subscriptionStatus: state.user.rtvPlusSubscriptionStatus,
  subscriptionData: state.user.rtvPlusSubscriptionData,
  subscriptionError: state.user.error,
  isLoggedIn: state.auth.isLoggedIn,
  userEmail:
    state.user.user?.email ||
    state.user.privateStats?.email ||
    state.auth.user?.email
});

const mapDispatchToProps = {
  subscribeToRtvPlus,
  resetRtvPlusSubscriptionState
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RTVPlusSubscribe)
);
